/**
 * Pipeline Calculations - UNIFIED CALCULATION FUNCTIONS
 *
 * All pipeline value and probability calculations are defined here.
 * DO NOT implement pipeline calculations anywhere else in the codebase.
 *
 * This ensures Dashboard, Analytics, PortfolioAnalytics, and Reports
 * all show identical numbers for the same data.
 */

import { Deal, DealStage } from '../types/deal'
import {
  STAGE_PROBABILITIES,
  ACTIVE_STAGES,
  WON_STAGE,
  LOST_STAGE,
} from '../constants/dealStages'

/**
 * Get the probability for a deal.
 *
 * A settled deal is not a forecast. `probability` is whatever the deal carried
 * while it was still in play and the app never revises it on a stage change,
 * so honouring it on a finished deal counts money that will never arrive
 * (Lost) and discounts money already won (Executed). On this book every
 * Executed and Lost deal still stores 40, left over from Negotiating: the desk
 * read an executed $5.0M as $2.0M weighted, and a lost $2.5M as $1.0M of
 * pipeline.
 *
 * Pipeline.tsx worked this out and fixed it inside its own weightedValue,
 * which left every other caller wrong - deal detail, deal comparison, the
 * revenue projections and both commission figures in the Reports PDF. The
 * rule belongs here, where all of them read it.
 *
 * An open deal still honours a probability an agent set by hand; that is the
 * point of the field.
 *
 * @param deal - The deal to get probability for
 * @returns Probability as a percentage (0-100)
 */
export function getDealProbability(deal: Deal): number {
  if (deal.stage === 'Executed' || deal.stage === 'Lost') {
    return STAGE_PROBABILITIES[deal.stage]
  }
  if (deal.probability != null) {
    return deal.probability
  }
  return STAGE_PROBABILITIES[deal.stage] ?? 10
}

/**
 * Check if a deal is in the active pipeline (not executed or lost).
 *
 * @param deal - The deal to check
 * @returns True if the deal is active
 */
export function isActiveDeal(deal: Deal): boolean {
  return ACTIVE_STAGES.includes(deal.stage)
}

/**
 * Check if a deal is won (executed).
 *
 * @param deal - The deal to check
 * @returns True if the deal is won
 */
export function isWonDeal(deal: Deal): boolean {
  return deal.stage === WON_STAGE
}

/**
 * Check if a deal is lost.
 *
 * @param deal - The deal to check
 * @returns True if the deal is lost
 */
export function isLostDeal(deal: Deal): boolean {
  return deal.stage === LOST_STAGE
}

/**
 * Calculate the weighted pipeline value.
 * This is the total value of active deals, weighted by their probability of closing.
 *
 * Formula: sum(deal.value * deal.probability / 100) for all active deals
 *
 * @param deals - Array of deals to calculate from
 * @returns Weighted pipeline value in dollars
 */
export function calculateWeightedPipelineValue(deals: Deal[]): number {
  return deals
    .filter((d) => isActiveDeal(d) && d.value != null)
    .reduce((sum, deal) => {
      const probability = getDealProbability(deal)
      return sum + (deal.value! * probability) / 100
    }, 0)
}

/**
 * Calculate the total (unweighted) pipeline value.
 * This is the sum of all active deal values without probability weighting.
 *
 * @param deals - Array of deals to calculate from
 * @returns Total pipeline value in dollars
 */
export function calculateTotalPipelineValue(deals: Deal[]): number {
  return deals
    .filter((d) => isActiveDeal(d) && d.value != null)
    .reduce((sum, deal) => sum + deal.value!, 0)
}

/**
 * Calculate pipeline metrics broken down by stage.
 *
 * @param deals - Array of deals to calculate from
 * @returns Object with count, value, and weighted value per stage
 */
export function calculatePipelineByStage(deals: Deal[]): Record<
  DealStage,
  {
    count: number
    value: number
    weighted: number
  }
> {
  const result = {} as Record<
    DealStage,
    { count: number; value: number; weighted: number }
  >

  // Initialize all stages with zeros
  for (const stage of Object.keys(STAGE_PROBABILITIES) as DealStage[]) {
    result[stage] = { count: 0, value: 0, weighted: 0 }
  }

  deals.forEach((deal) => {
    const value = deal.value ?? 0
    const weighted = (value * getDealProbability(deal)) / 100

    result[deal.stage].count++
    result[deal.stage].value += value
    result[deal.stage].weighted += weighted
  })

  return result
}

/**
 * Calculate commission-based pipeline metrics.
 * This calculates potential commission revenue from the pipeline.
 *
 * @param deals - Array of deals to calculate from
 * @returns Object with total potential, weighted, and by-stage breakdown
 */
export function calculateCommissionPipeline(deals: Deal[]): {
  totalPotential: number
  weighted: number
  byStage: Record<string, number>
} {
  const activeDeals = deals.filter((d) => isActiveDeal(d))
  const byStage: Record<string, number> = {}

  let totalPotential = 0
  let weighted = 0

  activeDeals.forEach((deal) => {
    const dealValue = deal.value || 0
    const commissionRate = deal.commission_rate || 0
    const probability = getDealProbability(deal)

    const potentialCommission = dealValue * (commissionRate / 100)
    const weightedCommission = potentialCommission * (probability / 100)

    byStage[deal.stage] = (byStage[deal.stage] || 0) + potentialCommission
    totalPotential += potentialCommission
    weighted += weightedCommission
  })

  return { totalPotential, weighted, byStage }
}

/**
 * Calculate the win rate for a set of deals.
 * Win rate = won deals / (won deals + lost deals)
 *
 * @param deals - Array of deals to calculate from
 * @returns Win rate as a percentage (0-100)
 */
export function calculateWinRate(deals: Deal[]): number {
  const wonDeals = deals.filter((d) => isWonDeal(d)).length
  const lostDeals = deals.filter((d) => isLostDeal(d)).length
  const totalClosed = wonDeals + lostDeals

  if (totalClosed === 0) return 0
  return (wonDeals / totalClosed) * 100
}

/**
 * Calculate average deal value for active pipeline.
 *
 * @param deals - Array of deals to calculate from
 * @returns Average deal value in dollars
 */
export function calculateAverageDealValue(deals: Deal[]): number {
  const activeDeals = deals.filter((d) => isActiveDeal(d) && d.value != null)
  if (activeDeals.length === 0) return 0

  const totalValue = activeDeals.reduce((sum, d) => sum + d.value!, 0)
  return totalValue / activeDeals.length
}

/**
 * Calculate projected earnings for deals expected to close within N days.
 *
 * @param deals - Array of deals to calculate from
 * @param days - Number of days to project (default 30)
 * @returns Projected earnings in dollars
 */
export function calculateProjectedEarnings(
  deals: Deal[],
  days: number = 30
): number {
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() + days)

  return deals
    .filter(
      (d) =>
        isActiveDeal(d) &&
        d.expected_close &&
        new Date(d.expected_close) <= cutoffDate
    )
    .reduce((sum, deal) => {
      const dealValue = deal.value || 0
      const commissionRate = deal.commission_rate || 0
      const probability = getDealProbability(deal)
      return sum + dealValue * (commissionRate / 100) * (probability / 100)
    }, 0)
}

/**
 * Calculate deal velocity metrics.
 * Measures how quickly deals move through the pipeline.
 *
 * @param deals - Array of deals to calculate from
 * @returns Object with velocity metrics
 */
export function calculateDealVelocity(deals: Deal[]): {
  avgDaysToClose: number
  byDealType: Record<string, number>
  byStage: Record<string, number>
} {
  const closedDeals = deals.filter(
    (d) => (isWonDeal(d) || isLostDeal(d)) && d.created && (d.actual_close_date || d.updated)
  )

  // Calculate average days to close
  let totalDays = 0
  closedDeals.forEach((deal) => {
    const startDate = new Date(deal.created)
    const endDate = new Date(deal.actual_close_date || deal.updated)
    const days = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))
    totalDays += days
  })
  const avgDaysToClose = closedDeals.length > 0 ? totalDays / closedDeals.length : 0

  // Calculate by deal type
  const byDealType: Record<string, number> = {}
  const dealTypeCount: Record<string, number> = {}

  closedDeals.forEach((deal) => {
    const dealType = deal.deal_type || 'other'
    const startDate = new Date(deal.created)
    const endDate = new Date(deal.actual_close_date || deal.updated)
    const days = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))

    byDealType[dealType] = (byDealType[dealType] || 0) + days
    dealTypeCount[dealType] = (dealTypeCount[dealType] || 0) + 1
  })

  // Convert totals to averages
  for (const dealType of Object.keys(byDealType)) {
    byDealType[dealType] = byDealType[dealType] / dealTypeCount[dealType]
  }

  // Calculate average time in each stage (using stage_changed_at if available)
  const byStage: Record<string, number> = {}
  const activeDeals = deals.filter((d) => isActiveDeal(d))

  activeDeals.forEach((deal) => {
    const stageStartDate = (deal as any).stage_changed_at || deal.updated || deal.created
    if (!stageStartDate) return

    const days = Math.max(0, Math.floor((Date.now() - new Date(stageStartDate).getTime()) / (1000 * 60 * 60 * 24)))
    const stage = deal.stage

    if (!byStage[stage]) {
      byStage[stage] = 0
    }
    byStage[stage] = Math.max(byStage[stage], days) // Track longest time in stage
  })

  return {
    avgDaysToClose,
    byDealType,
    byStage,
  }
}

/**
 * Calculate deal conversion rates between stages.
 *
 * @param deals - Array of deals to calculate from
 * @returns Conversion rate between stages
 */
export function calculateStageConversionRates(deals: Deal[]): Record<string, number> {
  const stageOrder = ['Prospect', 'Outreach', 'Negotiating', 'Agreed', 'Contract Sent', 'Executed']
  const stageCounts: Record<string, number> = {}

  deals.forEach((deal) => {
    const stageIndex = stageOrder.indexOf(deal.stage)
    if (stageIndex === -1) return

    // Count deals that reached each stage
    for (let i = 0; i <= stageIndex; i++) {
      stageCounts[stageOrder[i]] = (stageCounts[stageOrder[i]] || 0) + 1
    }
  })

  // Calculate conversion rates
  const conversionRates: Record<string, number> = {}
  for (let i = 1; i < stageOrder.length; i++) {
    const prevStage = stageOrder[i - 1]
    const currentStage = stageOrder[i]
    const prevCount = stageCounts[prevStage] || 0
    const currentCount = stageCounts[currentStage] || 0

    if (prevCount > 0) {
      conversionRates[`${prevStage} → ${currentStage}`] = (currentCount / prevCount) * 100
    }
  }

  return conversionRates
}

/**
 * Get summary statistics for a set of deals.
 *
 * @param deals - Array of deals to calculate from
 * @returns Summary object with key metrics
 */
export function getDealsSummary(deals: Deal[]): {
  totalDeals: number
  activeDeals: number
  wonDeals: number
  lostDeals: number
  totalPipelineValue: number
  weightedPipelineValue: number
  winRate: number
  averageDealValue: number
} {
  const activeDeals = deals.filter((d) => isActiveDeal(d))
  const wonDeals = deals.filter((d) => isWonDeal(d))
  const lostDeals = deals.filter((d) => isLostDeal(d))

  return {
    totalDeals: deals.length,
    activeDeals: activeDeals.length,
    wonDeals: wonDeals.length,
    lostDeals: lostDeals.length,
    totalPipelineValue: calculateTotalPipelineValue(deals),
    weightedPipelineValue: calculateWeightedPipelineValue(deals),
    winRate: calculateWinRate(deals),
    averageDealValue: calculateAverageDealValue(deals),
  }
}
